import { useEffect, useState } from 'react';

export type HashRoute = {
  path: string;
  query: URLSearchParams;
  match: RouteMatch;
};

export type RouteMatch =
  | { kind: 'home' }
  | { kind: 'persona-list' }
  | { kind: 'persona-workbench'; personaId: string }
  | { kind: 'persona-detail-alias'; personaId: string; redirectTo: string }
  | { kind: 'prompt-module-list' }
  | { kind: 'prompt-module-detail'; moduleId: string }
  | { kind: 'prompt-assignments'; personaId: string }
  | { kind: 'runtime-evidence' }
  | { kind: 'runtime-engagement-detail'; engagementId: string }
  | { kind: 'runtime-session-detail'; sessionId: string }
  | { kind: 'runtime-profile-detail'; sessionId: string }
  | { kind: 'runtime-audit-list' }
  | { kind: 'runtime-audit-detail'; eventId: string }
  | { kind: 'skill-library-list' }
  | { kind: 'skill-library-detail'; skillId: string }
  | { kind: 'loaded-skills-list' }
  | { kind: 'loaded-skill-detail'; personaId: string; skillName: string }
  | { kind: 'mcp-server-list' }
  | { kind: 'mcp-server-detail'; mcpId: string }
  | { kind: 'mcp-preset-list' }
  | { kind: 'mcp-preset-detail'; presetName: string }
  | { kind: 'tool-proxy-list' }
  | { kind: 'tool-proxy-detail'; personaId: string; serverName: string }
  | { kind: 'system-settings' }
  | { kind: 'system-status' }
  | { kind: 'release-operations' }
  | { kind: 'unknown' };

export function parseHashRoute(hash: string): HashRoute {
  const raw = hash.replace(/^#/, '') || '/';
  const normalized = raw.startsWith('/') ? raw : `/${raw}`;
  const [path, search = ''] = normalized.split('?', 2);
  const query = new URLSearchParams(search);
  const match = matchRoute(path);
  return { path, query, match };
}

function matchRoute(path: string): RouteMatch {
  const workbenchMatch = path.match(/^\/personas\/([^/]+)\/workbench$/);
  if (workbenchMatch) {
    return { kind: 'persona-workbench', personaId: decodeURIComponent(workbenchMatch[1]) };
  }
  const assignmentsMatch = path.match(/^\/personas\/([^/]+)\/prompts$/);
  if (assignmentsMatch) {
    return { kind: 'prompt-assignments', personaId: decodeURIComponent(assignmentsMatch[1]) };
  }
  const personaAliasMatch = path.match(/^\/personas\/([^/]+)$/);
  if (personaAliasMatch) {
    const personaId = decodeURIComponent(personaAliasMatch[1]);
    return {
      kind: 'persona-detail-alias',
      personaId,
      redirectTo: `#/personas/${encodeURIComponent(personaId)}/workbench`,
    };
  }
  const promptMatch = path.match(/^\/prompts\/([^/]+)$/);
  if (promptMatch) {
    return { kind: 'prompt-module-detail', moduleId: decodeURIComponent(promptMatch[1]) };
  }
  const runtimeEngagementMatch = path.match(/^\/runtime\/engagements\/([^/]+)$/);
  if (runtimeEngagementMatch) {
    return { kind: 'runtime-engagement-detail', engagementId: decodeURIComponent(runtimeEngagementMatch[1]) };
  }
  const runtimeProfileMatch = path.match(/^\/runtime\/sessions\/([^/]+)\/profile$/);
  if (runtimeProfileMatch) {
    return { kind: 'runtime-profile-detail', sessionId: decodeURIComponent(runtimeProfileMatch[1]) };
  }
  const runtimeSessionMatch = path.match(/^\/runtime\/sessions\/([^/]+)$/);
  if (runtimeSessionMatch) {
    return { kind: 'runtime-session-detail', sessionId: decodeURIComponent(runtimeSessionMatch[1]) };
  }
  const runtimeAuditMatch = path.match(/^\/runtime\/audit\/([^/]+)$/);
  if (runtimeAuditMatch) {
    return { kind: 'runtime-audit-detail', eventId: decodeURIComponent(runtimeAuditMatch[1]) };
  }
  const skillDetailMatch = path.match(/^\/skills\/library\/([^/]+)$/);
  if (skillDetailMatch) {
    return { kind: 'skill-library-detail', skillId: decodeURIComponent(skillDetailMatch[1]) };
  }
  const loadedSkillMatch = path.match(/^\/skills\/loaded\/([^/]+)\/([^/]+)$/);
  if (loadedSkillMatch) {
    return {
      kind: 'loaded-skill-detail',
      personaId: decodeURIComponent(loadedSkillMatch[1]),
      skillName: decodeURIComponent(loadedSkillMatch[2]),
    };
  }
  const mcpServerMatch = path.match(/^\/tools\/mcp-servers\/([^/]+)$/);
  if (mcpServerMatch) {
    return { kind: 'mcp-server-detail', mcpId: decodeURIComponent(mcpServerMatch[1]) };
  }
  const mcpPresetMatch = path.match(/^\/tools\/mcp-presets\/([^/]+)$/);
  if (mcpPresetMatch) {
    return { kind: 'mcp-preset-detail', presetName: decodeURIComponent(mcpPresetMatch[1]) };
  }
  const toolProxyMatch = path.match(/^\/tools\/tool-proxy\/([^/]+)\/([^/]+)$/);
  if (toolProxyMatch) {
    return {
      kind: 'tool-proxy-detail',
      personaId: decodeURIComponent(toolProxyMatch[1]),
      serverName: decodeURIComponent(toolProxyMatch[2]),
    };
  }
  if (path === '/runtime/audit') return { kind: 'runtime-audit-list' };
  if (path === '/runtime') return { kind: 'runtime-evidence' };
  if (path === '/skills' || path === '/skills/library') return { kind: 'skill-library-list' };
  if (path === '/skills/loaded') return { kind: 'loaded-skills-list' };
  if (path === '/tools' || path === '/tools/mcp-servers') return { kind: 'mcp-server-list' };
  if (path === '/tools/mcp-presets') return { kind: 'mcp-preset-list' };
  if (path === '/tools/tool-proxy') return { kind: 'tool-proxy-list' };
  if (path === '/system/status' || path === '/settings/status') return { kind: 'system-status' };
  if (path === '/system/release-operations' || path === '/settings/release-operations') return { kind: 'release-operations' };
  if (path === '/system' || path === '/settings') return { kind: 'system-settings' };
  if (path === '/personas') return { kind: 'persona-list' };
  if (path === '/prompts') return { kind: 'prompt-module-list' };
  if (path === '/') return { kind: 'home' };
  return { kind: 'unknown' };
}

function currentRoute(): HashRoute {
  return parseHashRoute(window.location.hash);
}

export function useHashRoute() {
  const [route, setRoute] = useState<HashRoute>(() => currentRoute());

  useEffect(() => {
    const onHashChange = () => setRoute(currentRoute());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  return route;
}
